import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ShoppingCart } from 'lucide-react';
import { usePromotionalModal } from './context/PromotionalModalContext';

const PromotionalModal = ({ isVisible, onClose }) => {
  const { banners, loading, error } = usePromotionalModal();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [shouldAutoShow, setShouldAutoShow] = useState(false);

  // Check if modal should auto-show on initial load
  useEffect(() => {
    // Check if this is the first time loading the page (not a reload)
    const hasShownModal = sessionStorage.getItem('promotionalModalShown');

    if (!hasShownModal && !loading && banners.length > 0) {
      setShouldAutoShow(true);
      sessionStorage.setItem('promotionalModalShown', 'true');
    }
  }, [loading, banners.length]);

  // Auto-slide effect
  useEffect(() => {
    const effectiveIsVisible = isVisible || shouldAutoShow;
    if (!effectiveIsVisible || banners.length <= 1) return;

    const slideTimer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % banners.length);
    }, 4000); // Change slide every 4 seconds

    return () => clearInterval(slideTimer);
  }, [isVisible, shouldAutoShow, banners.length]);

  // Reset slide when modal opens or banners change
  useEffect(() => {
    setCurrentSlide(0);
  }, [isVisible, shouldAutoShow, banners]);

  const handleNextSlide = () => {
    setCurrentSlide(prev => (prev + 1) % banners.length);
  };

  const handlePrevSlide = () => {
    setCurrentSlide(prev => (prev - 1 + banners.length) % banners.length);
  };

  const handleImageClick = () => {
    // Navigate to products page when image is clicked
    window.location.href = '/products';
    handleClose();
  };

  const handleClose = () => {
    setShouldAutoShow(false);
    if (onClose) {
      onClose();
    }
  };

  // Determine if modal should be visible
  const effectiveIsVisible = isVisible || shouldAutoShow;

  // Don't show modal if not visible or no banners
  if (!effectiveIsVisible || (!loading && banners.length === 0)) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl shadow-2xl bg-white"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-50 p-2 bg-black bg-opacity-20 backdrop-blur-sm rounded-full text-white hover:bg-opacity-40 transition-all duration-200 group"
            aria-label="Close modal"
          >
            <X className="w-2 h-2 sm:w-6 sm:h-6 group-hover:rotate-90 transition-transform duration-200" />
          </button>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
              <span className="ml-4 text-gray-600">Loading banners...</span>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="flex items-center justify-center h-96 text-center p-8">
              <div>
                <div className="text-red-500 text-xl mb-2">⚠️</div>
                <p className="text-gray-600">Failed to load promotional banners</p>
                <p className="text-sm text-gray-500 mt-2">{error}</p>
              </div>
            </div>
          )}

          {/* Banner Content */}
          {!loading && !error && banners.length > 0 && (
            <div className="relative h-full ">
              {/* Main Banner Image */}
              <div className="relative w-full h-full overflow-hidden">
                <motion.img
                  key={currentSlide}
                  initial={{ opacity: 0, scale: 1.1 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.5 }}
                  src={banners[currentSlide]?.imageUrl}
                  alt={banners[currentSlide]?.title || `Promotional Banner ${currentSlide + 1}`}
                  className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
                  onClick={handleImageClick}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />

                {/* Gradient Overlay for better text visibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent pointer-events-none"></div>
              </div>

              {/* Navigation Arrows (only show if more than 1 banner) */}
              {banners.length > 1 && (
                <>
                  <button
                    onClick={handlePrevSlide}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 p-3 bg-black bg-opacity-40 backdrop-blur-sm rounded-full text-white hover:bg-opacity-60 transition-all duration-200 group"
                    aria-label="Previous banner"
                  >
                    <ChevronLeft className="w-2 h-2 sm:w-6 sm:h-6 group-hover:-translate-x-1 transition-transform duration-200" />
                  </button>

                  <button
                    onClick={handleNextSlide}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 p-3 bg-black bg-opacity-40 backdrop-blur-sm rounded-full text-white hover:bg-opacity-60 transition-all duration-200 group"
                    aria-label="Next banner"
                  >
                    <ChevronRight className="w-2 h-2 sm:w-6 sm:h-6 group-hover:translate-x-1 transition-transform duration-200" />
                  </button>
                </>
              )}

              {/* Slide Indicators (only show if more than 1 banner) */}
              {banners.length > 1 && (
                <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex gap-2">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-200 ${
                        index === currentSlide
                          ? 'bg-white scale-125 shadow-lg'
                          : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                      }`}
                      aria-label={`Go to banner ${index + 1}`}
                    />
                  ))}
                </div>
              )}

              {/* Click to Shop Overlay */}
              <div className="absolute bottom-4 right-4">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                  className="px-4 py-2 hidden lg:flex bg-teal-800 hover:bg-teal-900 hover:scale-105 bg-opacity-90 backdrop-blur-sm rounded-full text-sm font-medium text-white shadow-lg cursor-pointer hover:bg-opacity-100 transition-all duration-200 group"
                  onClick={handleImageClick}
                >
                  <ShoppingCart className="group-hover:translate-x-1 transition-transform w-5 h-5 mr-2" />
                  Click to Shop
                </motion.div>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PromotionalModal;
